/**
 * State-based checks. RULES section 8. Run after every resolution, every
 * damage step and every action, looped until nothing changes.
 */

import { profileOf } from './cardDb';
import { getEffectiveStats } from './query';
import {
  ceaseIfToken,
  destroyObject,
  dieObject,
  emit,
  legendaryRuleRemove,
  type Ctx,
} from './mutate';
import { DRAINER_MARK } from './mutate';
import type { GameState } from './types';

/** Run one sweep. Returns true when it changed anything. */
function sweep(ctx: Ctx): boolean {
  const state = ctx.state;
  let changed = false;

  // 1. Integrity <= 0.
  for (const pid of state.playerOrder) {
    const p = state.players[pid];
    if (p.integrity <= 0 && !p.lost) {
      p.lost = true;
      changed = true;
      emit(ctx, { type: 'PlayerLost', turn: state.turn, player: pid, reason: 'integrity' });
    }
  }

  // 3 before 2: Health <= 0 kills even through HODL.
  for (const pid of state.playerOrder) {
    for (const id of state.players[pid].battlefield.characters.slice()) {
      const o = state.objects[id];
      if (!o || o.zone !== 'battlefield') continue;
      const stats = getEffectiveStats(state, id);
      if (stats.health <= 0) {
        if (dieObject(ctx, id)) changed = true;
      }
    }
  }

  // 2. Damage >= Health, and Drainer Link marks. HODL prevents both.
  for (const pid of state.playerOrder) {
    for (const id of state.players[pid].battlefield.characters.slice()) {
      const o = state.objects[id];
      if (!o || o.zone !== 'battlefield') continue;
      const stats = getEffectiveStats(state, id);
      const marked = (o.counters[DRAINER_MARK] ?? 0) > 0;
      if (o.damage >= stats.health || marked) {
        if (stats.keywords.includes('hodl')) {
          // HODL survives. Clear the mark so it is not re-checked forever.
          if (marked) {
            delete o.counters[DRAINER_MARK];
            changed = true;
          }
          continue;
        }
        if (destroyObject(ctx, id, marked ? 'drainer link' : 'lethal damage')) changed = true;
      }
    }
  }

  // 4. Upgrades with no host.
  for (const pid of state.playerOrder) {
    for (const id of state.players[pid].battlefield.upgrades.slice()) {
      const o = state.objects[id];
      if (!o || o.zone !== 'battlefield') continue;
      const host = o.attachedTo ? state.objects[o.attachedTo] : undefined;
      const hostAlive =
        host !== undefined &&
        host.zone === 'battlefield' &&
        profileOf(host).type === 'character';
      if (!hostAlive) {
        if (dieObject(ctx, id)) changed = true;
      }
    }
  }

  // 5. Legendary rule.
  for (const pid of state.playerOrder) {
    const p = state.players[pid];
    const rows = [
      p.battlefield.characters,
      p.battlefield.protocols,
      p.battlefield.upgrades,
      p.battlefield.wallets,
    ];
    for (const row of rows) {
      const byName = new Map<string, string[]>();
      for (const id of row) {
        const o = state.objects[id];
        if (!o) continue;
        const prof = profileOf(o);
        if (!prof.legendary) continue;
        const list = byName.get(prof.name) ?? [];
        list.push(id);
        byName.set(prof.name, list);
      }
      for (const ids of byName.values()) {
        if (ids.length < 2) continue;
        // Row order is entry order, so everything but the last entered goes.
        for (const id of ids.slice(0, ids.length - 1)) {
          legendaryRuleRemove(ctx, id);
          changed = true;
        }
      }
    }
  }

  // 6. Tokens outside the battlefield cease to exist.
  for (const id of Object.keys(state.objects)) {
    const o = state.objects[id];
    if (!o.tokenId) continue;
    if (o.zone === 'battlefield' || o.zone === 'limbo') continue;
    ceaseIfToken(ctx, id);
    changed = true;
  }

  return changed;
}

/** Loop the checks until the state is stable, then settle the game result. */
export function runStateBasedChecks(ctx: Ctx, maxPasses = 50): void {
  let passes = 0;
  while (sweep(ctx) && passes < maxPasses) passes++;
  settleGameResult(ctx);
}

/** A concede or an Integrity loss ends the game. Both at once is a draw. */
export function settleGameResult(ctx: Ctx): void {
  const state = ctx.state;
  if (state.phase === 'over') return;
  const losers = state.playerOrder.filter((pid) => state.players[pid].lost || state.players[pid].conceded);
  if (losers.length === 0) return;
  if (losers.length >= state.playerOrder.length) {
    state.draw = true;
    state.winner = null;
  } else {
    state.winner = state.playerOrder.find((pid) => !losers.includes(pid)) ?? null;
    state.draw = false;
  }
  state.phase = 'over';
  state.stack = [];
  state.pendingChoices = [];
  state.pendingTriggers = [];
  emit(ctx, {
    type: 'GameEnded',
    turn: state.turn,
    player: state.activePlayer,
    winner: state.winner,
    draw: state.draw,
  });
}

/** Violations the invariant checker cares about, exported for reuse. */
export function isGameOver(state: GameState): boolean {
  return state.phase === 'over';
}
